import React from 'react';
import { Box, Tabs, Tab, colors } from '@mui/material';

const HumbeeTabs = ({
  data = [],
  currentTab = 0,
  onChange,
  getTabLabel,
  getTabKey,
  getTabIcon,
  getTabDisabled,
  iconPosition = "start",
  renderTabContent,
  tabSx = {},
  tabsSx = {},
  tabPanelSx = {},
  variant = "scrollable",
  scrollButtons = "auto",
  tabVariant = "default", // small || default
  tabType = "dashed", // standard || boxed || dashed || outlined
  scrollVisible = false,
  visibleTabCount
}) => {
  
  const isScrollVisible = typeof scrollVisible === 'number' 
      ? data.length > scrollVisible 
      : scrollVisible;

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Tabs 
        value={currentTab} 
        onChange={onChange}
        sx={{ 
            p: '0 !important',
            minHeight: '32px',
            '& .MuiTabs-indicator': {
                display: tabType === 'dashed' ? 'block' : 'none',
                backgroundColor: '#995A00',
            },
            '& .MuiTabs-scrollButtons': {
                display: isScrollVisible ? 'flex' : 'none',
                width: 'auto',
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
            },
            ...(tabType === 'boxed' && {
                backgroundColor: '#D9D9D9',
                borderRadius: 1,
                padding: 1,
                minHeight: 'unset',
            }),
            ...(tabType === 'outlined' && {
                // border:  '1px solid #333',
                borderRadius: 1,
                padding: .5,
                boxShadow: 'inset 0px 0px 0px 1px #8C8C8C',
            }),
            ...tabsSx,
        }}
        variant={variant}
        scrollButtons={isScrollVisible ? true : scrollButtons}
        allowScrollButtonsMobile={isScrollVisible}
      >
        {data.map((item, index) => {
          const isSelected = currentTab === index;
          const icon = getTabIcon ? getTabIcon(item, index, isSelected) : null;
          const isDisabled = getTabDisabled ? getTabDisabled(item, index) : (item.disabled || false);
          
          let variantStyles = {};
          
          switch (tabType) {
            case 'boxed':
                variantStyles = {
                    borderRadius: 1,
                    borderBottom: 'none',
                    '&:hover': {
                      backgroundColor: '#FFF',
                      color: '#BF7000',
                    },
                    '&:disabled': {
                      backgroundColor: '#FFF',
                      color: '#D9D9D9',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#FFF',
                        color: '#995A00',
                    },
                    
                };
                break;
            case 'outlined':
                variantStyles = {
                  borderBottom: 'none',
                  '&:hover': {
                    backgroundColor: '#995A000D',
                    color: '#995A00',
                    borderRadius: 1,
                  },
                  '&:disabled': {
                    color: '#D9D9D9',
                    borderRadius: 1,
                  },
                  '&.Mui-selected': {
                    color: '#995A00',
                    borderRadius: 1,
                    backgroundColor: '#995A001F',
                    borderBottom: 'none',
                  },
                };
                break;
            case 'dashed':
                variantStyles = {
                    borderBottom: '1px solid #8C8C8C',
                    '&:hover': {
                      color: '#BF7000',
                      borderBottom: '2px solid #BF7000',
                      '& .MuiTab-iconWrapper': {
                        color: '#BF7000 !important',
                      },
                      '& .MuiTab-iconWrapper svg': {
                        fill: '#BF7000 !important',
                      },
                    },
                    '&:disabled': {
                      color: '#D9D9D9',
                      borderBottom: '1px solid #D9D9D9',
                    },
                    '&.Mui-selected': {
                      color: '#995A00',
                      borderBottom: '2px solid #995A00',
                      '& .MuiTab-iconWrapper': {
                        color: '#995A00 !important',
                      },
                      '& .MuiTab-iconWrapper svg': {
                        fill: '#995A00 !important',
                      },
                    },
                };
                break;
            case 'standard':
            default:
                variantStyles = {
                    borderBottom: 'none',
                    '&:hover': {
                      backgroundColor: '#995A000D',
                      color: '#995A00',
                      borderRadius: 1,

                    },
                    '&:disabled': {
                      color: '#D9D9D9',
                      borderRadius: 1,
                    },
                     '&.Mui-selected': {
                        color: '#995A00',
                        borderRadius: 1,
                        backgroundColor: '#995A001F',
                        borderBottom: 'none',
                    },
                };
                break;
          }

          return (
            <Tab 
              key={getTabKey ? getTabKey(item, index) : index}
              label={getTabLabel ? getTabLabel(item, index) : `Tab ${index + 1}`}
              icon={icon}
              iconPosition={iconPosition}
              disabled={isDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: tabVariant === 'small' ? '13px' : '16px',
                lineHeight: tabVariant === 'small' ? '20px' : '24px',
                minHeight: tabType === 'boxed' || 'outlined' ? '32px' : '40px',
               
                padding: tabVariant === 'small' ? '4px 12px' : '8px 24px' ,
                gap: '2px',
                color: '#333',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease-in-out',
                '& .MuiTab-iconWrapper': {
                    marginBottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: iconPosition === 'start' ? 0 : undefined,
                    marginLeft: iconPosition === 'end' ? 0 : undefined,
                },
                minWidth: (tabType === 'outlined' &&  tabVariant === 'small') && '50px',
                ...(visibleTabCount > 0 && {
                    maxWidth: `calc(100% / ${visibleTabCount})`,
                    minWidth: `calc(100% / ${visibleTabCount})`,
                    flexBasis: `calc(100% / ${visibleTabCount})`,
                    flexGrow: 0,
                    flexShrink: 0,
                    alignItems: 'center',
                }),
                ...variantStyles,
                ...tabSx
              }}
              disableRipple
            />
          );
        })}
      </Tabs>
      
      {renderTabContent && data.map((item, index) => (
        <Box
          key={getTabKey ? getTabKey(item, index) : index}
          role="tabpanel"
          hidden={currentTab !== index}
          sx={{
            display: currentTab === index ? 'block' : 'none',
            ...tabPanelSx
          }}
        >
          {currentTab === index && renderTabContent(item, index)}
        </Box>
      ))}
    </Box>
  );
};

export default HumbeeTabs;